import { Stack, Typography, Grid, Box } from "@mui/material";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import LuxorMed from "@/components/luxor/LuxorMed";
import { ManualInputForm } from "./components/ManualInputForm/ManualInputForm";
import { PendingExams } from "./components/PendingExams/PendingExams";
import { ExamCard } from "./components/ExamCard/ExamCard";
import { AddExamDialog } from "./components/AddExamDialog/AddExamDialog";
import { EditExamDialog } from "./components/EditExamDialog/EditExamDialog";
import {
  useLaboratoryState,
  useExamManagement,
  useExamDataProcessing,
  ExamResult,
} from "./hooks";
import dayjs from "dayjs";
import { useState } from "react";
import EditResultExam from "../Resultados/components/EditResultExam";

export function LaboratoryResults() {
  // Contexts
  const { control, unregister, luxorMode } = useMedicalCareContext();

  // Custom Hooks
  const {
    selectedAnalyte,
    editingIndex,
    isAddingExam,
    addingAnalyteName,
    selectedExam,
    isExtracting,
    isSubmitting,
    inputKey,
    setSelectedAnalyte,
    setEditingIndex,
    setIsAddingExam,
    setAddingAnalyteName,
    setSelectedExam,
    setIsExtracting,
    resetStates,
    incrementInputKey,
  } = useLaboratoryState();

  const {
    handleAddInput,
    handleAddExamForAnalyte,
    handleDeleteResult,
    formatNewExam,
    handleExamExtraction,
    handleAddExtractedExam,
    newExams,
    setValue,
  } = useExamManagement({
    control,
    unregister,
    incrementInputKey,
    setSelectedAnalyte,
    resetStates,
  });

  const { processHistoricalData, groupExamsByName } = useExamDataProcessing();

  // Data Processing
  const historicalData = processHistoricalData();
  const newExamsFormatted = newExams
    .map((exam: any, index: number) => {
      if (exam === "") return null;
      return formatNewExam(control._formValues?.newExames?.[index], index);
    })
    .filter(Boolean);

  const allExamResults = [
    ...newExamsFormatted,
    ...(historicalData || []),
  ].filter((exam) => exam?.item?.[2]?.answer?.[0]?.valueDate); // Filtra apenas exames com data

  const groupExams = groupExamsByName(allExamResults);

  const [dateNewAnalyte, setDateNewAnalyte] = useState<dayjs.Dayjs>(dayjs());

  // Estados para o diálogo de edição
  const [editResultExamOpen, setEditResultExamOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState<ExamResult | null>(null);
  const [analyteName, setAnalyteName] = useState<string>("");
  const [editedValue, setEditedValue] = useState<string>("");
  const [editedDate, setEditedDate] = useState<dayjs.Dayjs>(dayjs());
  const [editedUnit, setEditedUnit] = useState<string>("");

  const handleOpenEditDialog = (exam: ExamResult) => {
    setSelectedResult(exam);
    setAnalyteName(exam.item[0]?.answer?.[0]?.valueString || "");
    setEditedValue(exam.item[1]?.answer?.[0]?.valueString || "");

    const unitItem = exam.item?.find(
      (item) => item.text === "Unidade"
    );

    if (unitItem && unitItem.answer && unitItem.answer[0]?.valueString) {
      setEditedUnit(unitItem.answer[0].valueString);
    } else {
      setEditedUnit(exam.item[1]?.answer?.[0]?.valueString?.split(" ")?.[1] || "");
    }

    if (exam.item[2]?.answer?.[0]?.valueDate) {
      setEditedDate(dayjs(exam.item[2]?.answer?.[0]?.valueDate));
    }

    setEditResultExamOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditResultExamOpen(false);
    setSelectedResult(null);
  };

  const handleSaveEdit = () => {
    handleCloseEditDialog();

  };

  return (
    <Stack direction="row" width="100%" gap={2}>
      <Stack
        p={2}
        width={luxorMode ? "50%" : "100%"}
        overflow="auto"
        height="60vh"
      >
        {/* Área superior com input manual e exames pendentes */}
        <Stack direction="row" spacing={2}>
          <ManualInputForm
            control={control}
            inputKey={inputKey}
            newExamsLength={newExams.length}
            onAdd={handleAddInput}
            isSubmitting={isSubmitting}
            isDialogOpen={isAddingExam}
            dateNewAnalyte={dateNewAnalyte}
            setDateNewAnalyte={setDateNewAnalyte}
          />

          <PendingExams
            handleExamExtraction={handleExamExtraction}
            handleAddExtractedExam={handleAddExtractedExam}
          />
        </Stack>

        {/* Lista de Resultados */}
        <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
          Histórico de Resultados
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {Object.entries(groupExams).map(([analyte, exams]) => {
              const isSelected = selectedAnalyte === analyte;

              return (
                <Grid
                  item
                  xs={12}
                  sm={isSelected ? 12 : 6}
                  md={isSelected ? 12 : 6}
                  key={analyte}
                  sx={{
                    transition: "all 0.3s ease",
                  }}
                >
                  <ExamCard
                    analyte={analyte}
                    exams={exams}
                    isSelected={isSelected}
                    onSelect={setSelectedAnalyte}
                    onAddNew={(analyte) => {
                      setAddingAnalyteName(analyte);
                      setIsAddingExam(true);
                      setValue(`newExames.${newExams.length}.name`, analyte);
                    }}
                    onEdit={(index) => setEditingIndex(index)}
                    onDelete={handleDeleteResult}
                    handleOpenEditDialog={handleOpenEditDialog}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Stack>

      {/* Dialogs */}
      {editResultExamOpen && selectedResult && (
        <EditResultExam
          editDialogOpen={editResultExamOpen}
          handleCloseEditDialog={handleCloseEditDialog}
          selectedResult={selectedResult}
          handleSaveEdit={handleSaveEdit}
          name={analyteName}
          editedValue={editedValue}
          setEditedValue={setEditedValue}
          editedDate={editedDate}
          setEditedDate={setEditedDate}
          editedUnit={editedUnit}
          setEditedUnit={setEditedUnit}
        />
      )}

      {isAddingExam && (
        <AddExamDialog
          open={isAddingExam}
          analyteName={addingAnalyteName}
          onClose={() => {
            setIsAddingExam(false);
            setAddingAnalyteName(null);
            // Reset form values
            control.resetField(`newExames.${newExams.length}.value`);
            control.resetField(`newExames.${newExams.length}.unit`);
            control.resetField(`newExames.${newExams.length}.date`);
          }}
          onAdd={() => handleAddExamForAnalyte(addingAnalyteName)}
          isSubmitting={isSubmitting}
          control={control}
          inputKey={inputKey}
          newExamsLength={newExams.length}
        />
      )}

      {editingIndex !== null && (
        <EditExamDialog
          open={editingIndex !== null}
          onClose={() => setEditingIndex(null)}
          control={control}
          editingIndex={editingIndex}
          onSave={() => {
            console.log("save");
            setEditingIndex(null);
          }}
        />
      )}

      {luxorMode && (
        <Stack width="50%">
          <LuxorMed />
        </Stack>
      )}
    </Stack>
  );
}
